import { useState, useEffect } from 'react';
import api from '../api/client';
const cache = new Map();
const CACHE_TTL_MS = 30000;
/**
 * Fetches a feature switch value from the backend.
 * The result is cached for 30 seconds so page navigation doesn't hammer the API.
 * Falls back to `false` on error (fail-closed) so the UI degrades gracefully.
 *
 * The hook starts rendering immediately with the fallback value and updates
 * asynchronously — there is intentionally NO loading spinner gate so forms
 * are always interactive.
 */
export function useFeatureSwitch(feature) {
    const [enabled, setEnabled] = useState(false);
    const [config, setConfig] = useState({});
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        const fetchSwitch = async () => {
            const now = Date.now();
            const cached = cache.get(feature);
            if (cached && cached.expiresAt > now) {
                if (!cancelled) {
                    setEnabled(cached.enabled);
                    setConfig(cached.config);
                    setLoading(false);
                }
                return;
            }
            try {
                const res = await api.get(`/api/v1/feature-switches/${feature}`);
                // Guard against null/unexpected responses (e.g. in tests)
                const en = res.data?.enabled ?? false;
                const cfg = res.data?.config ?? {};
                cache.set(feature, { enabled: en, config: cfg, expiresAt: now + CACHE_TTL_MS });
                if (!cancelled) {
                    setEnabled(en);
                    setConfig(cfg);
                }
            }
            catch {
                // Fail closed: default to feature OFF
                if (!cancelled)
                    setEnabled(false);
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        };
        fetchSwitch();
        return () => { cancelled = true; };
    }, [feature]);
    return { enabled, config, loading };
}
